// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { type WeightUnitPlusTrays, getCurrencySymbol } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { formatOrderInput, getID } from '../../utils';
import { BlendIdLink } from '../blend';
import { Table } from '../common/table';
import { CropIdLink } from '../crop';

interface Input {
  readonly id: string;
  readonly quantity: string;
  readonly size_amount?: string | null;
  readonly unit: string;
  readonly price_per_item?: string | null;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
  readonly blend?: {
    readonly id: string;
    readonly blend_name: string;
    readonly status: string;
  } | null;
}

interface Props {
  readonly orderInputs: readonly Input[];
}

export default function OrderInputsTable({ orderInputs }: Props) {
  const { currencyCode } = useUserContext();
  const currencySymbol = getCurrencySymbol(currencyCode);

  const links: React.ReactElement[] = [];
  const amounts: string[] = [];
  const pricePerItem: string[] = [];
  const totalPrice: string[] = [];

  for (const input of orderInputs) {
    const inputStr = formatOrderInput({
      quantity: input.quantity,
      sizeAmount: input.size_amount,
      sizeUnit: input.unit as WeightUnitPlusTrays,
    });
    pricePerItem.push(
      input.price_per_item != null ? `${currencySymbol}${Number.parseFloat(input.price_per_item).toFixed(2)}` : '-',
    );
    totalPrice.push(
      input.price_per_item != null
        ? `${currencySymbol}${new Decimal(input.price_per_item).mul(input.quantity).toFixed(2, Decimal.ROUND_HALF_UP)}`
        : '-',
    );
    amounts.push(inputStr);
    if (input.blend != null) {
      links.push(<BlendIdLink id={getID(input.blend.id)} title={input.blend.blend_name} status={input.blend.status} />);
    } else if (input.crop != null) {
      links.push(<CropIdLink id={getID(input.crop.id)} name={input.crop.crop_name} />);
    }
  }
  const columns = [
    {
      name: 'Crop/Blend',
      values: links,
    },
    {
      name: 'Quantity',
      values: amounts,
    },
    {
      name: 'Unit Price',
      values: pricePerItem,
    },
    {
      name: 'Amount',
      values: totalPrice,
      visibleAt: 'sm' as const,
    },
  ];

  return <Table columns={columns} />;
}
